import { ElMessage } from 'element-plus'
import { useCopyToClipboard } from '@pureadmin/utils'

// 检查剪贴板API是否可用
const isClipboardAvailable = () => {
  return navigator && 'clipboard' in navigator && 'writeText' in navigator.clipboard
}

// 兼容性写入剪贴板函数
const writeTextToClipboard = async (text: string | { value: string; successInfo?: string }) => {
  const textValue = typeof text !== 'object' ? text : text.value
  if (isClipboardAvailable()) {
    return navigator.clipboard.writeText(textValue)
  } else {
    // 降级方案：使用document.execCommand方法
    // 注意：该方法已被废弃，但在某些旧浏览器中仍可使用
    try {
      const textArea = document.createElement('textarea')
      textArea.value = textValue
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      const success = document.execCommand('copy')
      document.body.removeChild(textArea)
      return success ? Promise.resolve() : Promise.reject(new Error('复制失败'))
    } catch (err) {
      return Promise.reject(err)
    }
  }
}

export const useClipboard = () => {
  const copyText = async (text: string) => {
    try {
      await writeTextToClipboard(text)
      ElMessage.success('复制成功')
    } catch (err) {
      ElMessage.error('复制失败')
      console.error('复制失败:', err)
    }
  }

  return {
    copyText,
  }
}

// 创建自定义指令 (使用@pureadmin/utils的useCopyToClipboard实现)
export const vCopy = {
  mounted(el: HTMLElement, binding: { value: string; successInfo?: string }) {
    el.style.cursor = 'pointer'
    // 添加提示元素
    const tooltip = document.createElement('div')
    tooltip.style.cssText = `
      position: absolute;
      background: var(--el-color-info-dark-2);
      color: white;
      padding: 8px 12px;
      border-radius: 4px;
      font-size: 12px;
      display: none;
      z-index: 9999;
      pointer-events: none;
      transform: translateX(-50%);
      transition: opacity 0.3s;
    `

    // 添加小箭头
    const arrow = document.createElement('div')
    arrow.style.cssText = `
      position: absolute;
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-top: 6px solid var(--el-color-info-dark-2);
      bottom: -6px;
      left: 50%;
      transform: translateX(-50%);
    `

    tooltip.textContent = '点击复制'
    tooltip.appendChild(arrow)
    document.body.appendChild(tooltip)

    // 显示提示
    const showTooltip = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect()
      tooltip.style.display = 'block'
      // 定位在元素上方中间
      tooltip.style.left = `${rect.left + rect.width / 2}px`
      tooltip.style.top = `${rect.top - tooltip.offsetHeight - 10}px`
    }

    // 隐藏提示
    const hideTooltip = () => {
      tooltip.style.display = 'none'
    }

    // 使用@pureadmin/utils的useCopyToClipboard
    const { copied, update } = useCopyToClipboard()

    // 复制处理
    const handler = async () => {
      const text = binding.value
      // 如果是对象，使用其value属性
      const textValue = typeof text === 'object' ? text.value : text

      await update(textValue)

      if (copied.value) {
        if (typeof text === 'object' && text.successInfo) {
          ElMessage.success(text.successInfo)
        } else {
          ElMessage.success('复制成功')
        }
      } else {
        ElMessage.error('复制失败')
      }
    }

    el.addEventListener('mouseenter', showTooltip)
    el.addEventListener('mouseleave', hideTooltip)
    el.addEventListener('click', handler)

    // 存储引用以便清理
    ;(el as any).__copyHandler__ = {
      handler,
      showTooltip,
      hideTooltip,
      tooltip,
    }
  },

  // 添加updated钩子函数，确保在数据更新时能更新复制内容
  updated(el: HTMLElement, binding: { value: string; oldValue: string }) {
    // 确保el上存在__copyHandler__属性
    if (!el || !(el as any).__copyHandler__) {
      return
    }

    // 值未变化则不更新
    if (binding.value === binding.oldValue) {
      return
    }

    // 获取旧的处理函数和其他信息
    const oldHandler = (el as any).__copyHandler__.handler

    // 使用@pureadmin/utils的useCopyToClipboard
    const { copied, update } = useCopyToClipboard()

    // 创建新的处理函数
    const newHandler = async () => {
      const text = binding.value
      // 如果是对象，使用其value属性
      const textValue = typeof text === 'object' ? text.value : text

      await update(textValue)

      if (copied.value) {
        if (typeof text === 'object' && text.successInfo) {
          ElMessage.success(text.successInfo)
        } else {
          ElMessage.success('复制成功')
        }
      } else {
        ElMessage.error('复制失败')
      }
    }

    // 移除旧的事件监听
    el.removeEventListener('click', oldHandler)

    // 添加新的事件监听
    el.addEventListener('click', newHandler)

    // 更新存储的引用
    ;(el as any).__copyHandler__.handler = newHandler
  },

  beforeUnmount(el: HTMLElement) {
    // 清理所有事件监听和DOM元素
    const { handler, showTooltip, hideTooltip, tooltip } = (el as any).__copyHandler__
    el.removeEventListener('click', handler)
    el.removeEventListener('mouseenter', showTooltip)
    el.removeEventListener('mouseleave', hideTooltip)
    document.body.removeChild(tooltip)
    delete (el as any).__copyHandler__
  },
}
